import { useState, useEffect } from 'react';
import { buyerSearchService } from '@/services/buyerSearchService';
import { BuyerSearchFilters, BuyerSearchResult } from '@/types/buyerSearch';
import { useToast } from '@/hooks/use-toast';

export const useBuyerSearch = () => {
  const [buyers, setBuyers] = useState<BuyerSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<BuyerSearchFilters>({});
  const { toast } = useToast();

  const ITEMS_PER_PAGE = 20;

  const searchBuyers = async (newFilters?: BuyerSearchFilters, page = 1) => {
    console.log('useBuyerSearch: Starting search with filters:', newFilters, 'page:', page);
    setIsLoading(true);
    
    const searchFilters = newFilters || filters;
    const offset = (page - 1) * ITEMS_PER_PAGE;
    
    try {
      console.log('useBuyerSearch: Calling buyerSearchService.searchBuyers');
      const { data, count, error } = await buyerSearchService.searchBuyers(
        searchFilters,
        ITEMS_PER_PAGE,
        offset
      );

      console.log('useBuyerSearch: Search result:', { data: data?.length, count, error });

      if (error) {
        console.error('useBuyerSearch: Search error:', error);
        toast({
          title: "Search Error",
          description: error,
          variant: "destructive"
        });
        return;
      }

      setBuyers(data);
      setTotalCount(count);
      setCurrentPage(page);
      
      if (newFilters) {
        setFilters(newFilters);
      }
    } catch (error) {
      console.error('useBuyerSearch: Caught error:', error);
      toast({
        title: "Search Error",
        description: "Failed to search buyers",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({});
    searchBuyers({}, 1);
  };

  const goToPage = (page: number) => {
    searchBuyers(filters, page);
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  // Initial load
  useEffect(() => {
    console.log('useBuyerSearch: Initializing search');
    searchBuyers({}, 1);
  }, []); // Empty dependency array is correct for initial load

  return {
    buyers,
    isLoading,
    totalCount,
    currentPage,
    totalPages,
    filters,
    setFilters,
    searchBuyers,
    resetFilters,
    goToPage,
    ITEMS_PER_PAGE
  };
};